import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid, Stack,
  Divider, IconButton, MenuItem, CircularProgress, Alert, Chip, Autocomplete,
  Select, FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedIcon from '@mui/icons-material/Verified';
import TimerIcon from '@mui/icons-material/Timer';
import { toast } from 'react-toastify';
import { createAstrologer } from '../../api/endpoints/astrologers';
import { uploadFile } from '../../api/endpoints/media';
import { sendPhoneOtp, sendEmailOtp, verifyPhoneOtp, verifyEmailOtp } from '../../api/endpoints/astrologers';
import countryCodes from '../../utils/countryCodes';

const categoryData = [
  { main: 'Astrologer', subs: ['Vedic Astrology', 'KP Astrology', 'Nadi Astrology', 'Western Astrology', 'Prashna Kundli', 'Horoscope Reading', 'Birth Chart Analysis', 'Face Reading'] },
  { main: 'Vastu Shastri', subs: ['Residential Vastu', 'Commercial Vastu', 'Office Vastu', 'Industrial Vastu', 'Factory Vastu', 'Shop Vastu', 'Plot Vastu', 'Interior Vastu', 'Feng Shui'] },
  { main: 'Tarot Card Reader', subs: ['Love Tarot', 'Career Tarot', 'Finance Tarot', 'Spiritual Tarot', 'Angel Tarot', 'Relationship Tarot'] },
  { main: 'Numerology', subs: ['Chaldean Numerology', 'Pythagorean Numerology', 'Mobile Numerology', 'Name Numerology', 'Business Numerology'] },
  { main: 'Crystal Healer', subs: ['Crystal Therapy', 'Chakra Healing', 'Aura Cleansing', 'Energy Healing'] },
  { main: 'Lamafera Master', subs: ['Energy Healing', 'Spiritual Healing', 'Aura Healing', 'Chakra Balancing'] },
  { main: 'Palmist', subs: ['Palm Reading', 'Hand Analysis', 'Face Reading'] },
  { main: 'Jain Astrologer', subs: ['Jain Horoscope', 'Jain Spiritual Guidance', 'Jain Numerology'] },
  { main: 'Laal Kitab', subs: ['Lal Kitab Remedies', 'Lal Kitab Horoscope', 'Lal Kitab Dosha'] },
  { main: 'Spiritual', subs: ['Spiritual Healing', 'Psychic Reading', 'Meditation', 'Energy Cleansing', 'Aura Reading'] },
  { main: 'Reiki Healer', subs: ['Reiki Level 1', 'Reiki Level 2', 'Reiki Master', 'Chakra Healing'] },
  { main: 'Pandit', subs: ['Grah Shanti Puja', 'Satyanarayan Katha', 'Rudrabhishek', 'Navgraha Puja', 'Marriage Puja', 'Griha Pravesh', 'Mundan Sanskar'] },
  { main: 'Puranacharya', subs: ['Bhagwat Katha', 'Ram Katha', 'Shiv Mahapuran', 'Devi Bhagwat'] },
];

const languageOptions = ['Hindi', 'English', 'Gujarati', 'Marathi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Punjabi', 'Urdu', 'Odia', 'Assamese', 'Sanskrit'];

const initialForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  niceName: '',
  mainCategories: [],
  specialization: [],
  experience: '',
  languages: [],
  bio: '',
  chatRate: '',
  callRate: '',
  videoCallRate: '',
  kycStatus: 'pending',
};

function FilePicker({ label, value, onChange, onRemove, error }) {
  return (
    <Box>
      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label} <span style={{ color: '#d32f2f' }}>*</span>
      </Typography>
      {value ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{
          p: 1, border: '1px solid', borderColor: 'success.light', borderRadius: 1, bgcolor: 'success.50',
        }}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value.name}
          </Typography>
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth sx={{ py: 1.2, borderStyle: 'dashed' }}>
          Choose file
          <input type="file" accept="image/*" hidden onChange={(e) => { if (e.target.files[0]) onChange(e.target.files[0]); }} />
        </Button>
      )}
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
}

export default function RegisterAstrologer() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [countryCode, setCountryCode] = useState('+91');
  const [files, setFiles] = useState({
    profileImage: null,
    aadhaarFront: null,
    aadhaarBack: null,
    panCard: null,
  });
  const [previews, setPreviews] = useState({
    profileImage: '',
    aadhaarFront: '',
    aadhaarBack: '',
    panCard: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  // Phone OTP state
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
  const [phoneOtpId, setPhoneOtpId] = useState(null);
  const [phoneOtpInput, setPhoneOtpInput] = useState('');
  const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
  const [verifyingPhoneOtp, setVerifyingPhoneOtp] = useState(false);

  // Email OTP state
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [emailOtpId, setEmailOtpId] = useState(null);
  const [emailOtpInput, setEmailOtpInput] = useState('');
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);

  const setField = (key) => (e, val) => {
    const value = val !== undefined ? val : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));

    // Reset OTP verification if phone/email changes after OTP sent
    if (key === 'phone' && phoneOtpSent) {
      setPhoneOtpSent(false);
      setPhoneOtpVerified(false);
      setPhoneOtpId(null);
      setPhoneOtpInput('');
      toast.info('Phone number changed. Please send OTP again.');
    }
    if (key === 'email' && emailOtpSent) {
      setEmailOtpSent(false);
      setEmailOtpVerified(false);
      setEmailOtpId(null);
      setEmailOtpInput('');
      toast.info('Email changed. Please send OTP again.');
    }
  };

  const handleFileChange = (key) => (file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    const reader = new FileReader();
    reader.onload = (e) => setPreviews((prev) => ({ ...prev, [key]: e.target.result }));
    reader.readAsDataURL(file);
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const handleFileRemove = (key) => () => {
    setFiles((prev) => ({ ...prev, [key]: null }));
    setPreviews((prev) => ({ ...prev, [key]: '' }));
  };

  const handleSendPhoneOtp = async () => {
    if (!form.phone.trim()) {
      setErrors((prev) => ({ ...prev, phone: 'Phone number is required' }));
      return;
    }
    const fullPhone = countryCode + form.phone.trim();
    setSendingPhoneOtp(true);
    try {
      const res = await sendPhoneOtp(fullPhone);
      setPhoneOtpId(res.data.data.otpId);
      setPhoneOtpSent(true);
      setPhoneOtpVerified(false);
      setPhoneOtpInput('');
      toast.success('OTP sent to phone and WhatsApp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingPhoneOtp(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!phoneOtpInput.trim() || phoneOtpInput.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }
    setVerifyingPhoneOtp(true);
    try {
      await verifyPhoneOtp(phoneOtpId, phoneOtpInput.trim());
      setPhoneOtpVerified(true);
      toast.success('Phone verified successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setVerifyingPhoneOtp(false);
    }
  };

  const handleSendEmailOtp = async () => {
    if (!form.email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Email is required' }));
      return;
    }
    setSendingEmailOtp(true);
    try {
      const res = await sendEmailOtp(form.email.trim().toLowerCase());
      setEmailOtpId(res.data.data.otpId);
      setEmailOtpSent(true);
      setEmailOtpVerified(false);
      setEmailOtpInput('');
      toast.success('OTP sent to email');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingEmailOtp(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtpInput.trim() || emailOtpInput.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }
    setVerifyingEmailOtp(true);
    try {
      await verifyEmailOtp(emailOtpId, emailOtpInput.trim());
      setEmailOtpVerified(true);
      toast.success('Email verified successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  const isFormValid = () => {
    return form.name.trim() &&
           form.email.trim() &&
           form.password &&
           form.phone.trim() &&
           form.mainCategories.length > 0 &&
           form.specialization.length > 0 &&
           form.experience !== '' &&
            form.languages.length && 
           form.bio.trim() &&
           form.chatRate &&
           form.callRate &&
           form.videoCallRate &&
           files.profileImage &&
           files.aadhaarFront &&
           files.aadhaarBack &&
           files.panCard &&
           phoneOtpVerified &&
           emailOtpVerified;
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 6) e.password = 'Min 6 characters';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    if (!form.mainCategories.length) e.mainCategories = 'Select at least 1 main category';
    if (!form.specialization.length) e.specialization = 'Select at least 1 sub category';
    if (form.experience === '') e.experience = 'Required';
    if (!form.languages.length) e.languages = 'Required';
    if (!form.bio.trim()) e.bio = 'Required';
    if (!form.chatRate) e.chatRate = 'Required';
    if (!form.callRate) e.callRate = 'Required';
    if (!form.videoCallRate) e.videoCallRate = 'Required';
    if (!files.profileImage) e.profileImage = 'Required';
    if (!files.aadhaarFront) e.aadhaarFront = 'Required';
    if (!files.aadhaarBack) e.aadhaarBack = 'Required';
    if (!files.panCard) e.panCard = 'Required';
    if (!phoneOtpVerified) e.phoneOtp = 'Phone OTP must be verified';
    if (!emailOtpVerified) e.emailOtp = 'Email OTP must be verified';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const uploadPromises = [
        uploadFile(files.profileImage).then((r) => r.data?.data?.url || r.data?.url).catch(() => null),
        uploadFile(files.aadhaarFront).then((r) => r.data?.data?.url || r.data?.url).catch(() => null),
        uploadFile(files.aadhaarBack).then((r) => r.data?.data?.url || r.data?.url).catch(() => null),
        uploadFile(files.panCard).then((r) => r.data?.data?.url || r.data?.url).catch(() => null),
        ...photoFiles.map((f) => uploadFile(f).then((r) => r.data?.data?.url || r.data?.url).catch(() => null)),
      ];
      const [profileImageUrl, aadhaarFrontUrl, aadhaarBackUrl, panCardUrl, ...photoUrls] = await Promise.all(uploadPromises);
      if (!profileImageUrl || !aadhaarFrontUrl || !aadhaarBackUrl || !panCardUrl) {
        toast.error('One or more document uploads failed. Please retry.');
        setSubmitting(false);
        return;
      }
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim() ? countryCode + form.phone.trim() : undefined,
        niceName: form.niceName.trim(),
        photos: photoUrls.filter(Boolean),
        mainCategories: form.mainCategories,
        specializations: form.specialization,
        experience: Number(form.experience),
        languages: form.languages,
        bio: form.bio.trim(),
        chatRate: Number(form.chatRate),
        callRate: Number(form.callRate),
        videoCallRate: Number(form.videoCallRate),
        profileImage: profileImageUrl,
        aadhaarFront: aadhaarFrontUrl,
        aadhaarBack: aadhaarBackUrl,
        panCard: panCardUrl,
        kycStatus: form.kycStatus,
      };
      const res = await createAstrologer(payload);
      toast.success(res.data?.message || 'Astrologer registered successfully');
      navigate('/astrologers');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <AutoAwesomeIcon sx={{ color: '#F5A623', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Register Astrologer</Typography>
          <Typography variant="body2" color="text.secondary">
            Create a new astrologer account on behalf of an astrologer
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Share the temporary password with the astrologer after registration. They can change it after first login.
      </Alert>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Account Details</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Full Name"
                    fullWidth required
                    value={form.name}
                    onChange={setField('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                        {countryCodes.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Phone"
                      fullWidth
                      value={form.phone}
                      onChange={(e) => setForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      placeholder="9876543210"
                      error={!!errors.phone}
                      helperText={errors.phone}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} sx={{ mt: '25px', flexWrap: 'wrap' }}>
                    {phoneOtpSent && !phoneOtpVerified ? (
                      <>
                        <TextField
                          label="Enter OTP"
                          fullWidth
                          size="small"
                          value={phoneOtpInput}
                          onChange={(e) => setPhoneOtpInput(e.target.value)}
                          placeholder="6-digit code"
                          InputProps={{
                            endAdornment: (
                              <Button
                                size="small"
                                variant={verifyingPhoneOtp ? 'outlined' : 'contained'}
                                onClick={handleVerifyPhoneOtp}
                                disabled={verifyingPhoneOtp || !phoneOtpInput}
                                startIcon={verifyingPhoneOtp ? <CircularProgress size={16} /> : <VerifiedIcon fontSize="small" />}
                              >
                                {verifyingPhoneOtp ? 'Verifying...' : 'Verify'}
                              </Button>
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleSendPhoneOtp}
                          disabled={sendingPhoneOtp || !form.phone.trim()}
                          startIcon={sendingPhoneOtp ? <CircularProgress size={16} /> : <TimerIcon fontSize="small" />}
                        >
                          {sendingPhoneOtp ? 'Resending...' : 'Resend OTP'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSendPhoneOtp}
                        disabled={sendingPhoneOtp || !form.phone.trim()}
                        startIcon={sendingPhoneOtp ? <CircularProgress size={16} /> : <TimerIcon fontSize="small" />}
                        sx={{ minWidth: 140 }}
                      >
                        {sendingPhoneOtp ? 'Sending...' : 'Send OTP (SMS + WhatsApp)'}
                      </Button>
                    )}
                    {phoneOtpVerified && (
                      <Chip
                        icon={<VerifiedIcon fontSize="small" />}
                        label="Phone Verified"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth required
                    value={form.email}
                    onChange={setField('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} sx={{ mt: '25px', flexWrap: 'wrap' }}>
                    {emailOtpSent && !emailOtpVerified ? (
                      <>
                        <TextField
                          label="Enter OTP"
                          fullWidth
                          size="small"
                          value={emailOtpInput}
                          onChange={(e) => setEmailOtpInput(e.target.value)}
                          placeholder="6-digit code"
                          InputProps={{
                            endAdornment: (
                              <Button
                                size="small"
                                variant={verifyingEmailOtp ? 'outlined' : 'contained'}
                                onClick={handleVerifyEmailOtp}
                                disabled={verifyingEmailOtp || !emailOtpInput}
                                startIcon={verifyingEmailOtp ? <CircularProgress size={16} /> : <VerifiedIcon fontSize="small" />}
                              >
                                {verifyingEmailOtp ? 'Verifying...' : 'Verify'}
                              </Button>
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleSendEmailOtp}
                          disabled={sendingEmailOtp || !form.email.trim()}
                          startIcon={sendingEmailOtp ? <CircularProgress size={16} /> : <TimerIcon fontSize="small" />}
                        >
                          {sendingEmailOtp ? 'Resending...' : 'Resend OTP'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSendEmailOtp}
                        disabled={sendingEmailOtp || !form.email.trim()}
                        startIcon={sendingEmailOtp ? <CircularProgress size={16} /> : <TimerIcon fontSize="small" />}
                        sx={{ minWidth: 140 }}
                      >
                        {sendingEmailOtp ? 'Sending...' : 'Send OTP (Email)'}
                      </Button>
                    )}
                    {emailOtpVerified && (
                      <Chip
                        icon={<VerifiedIcon fontSize="small" />}
                        label="Email Verified"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Temporary Password"
                    type="text"
                    fullWidth required
                    value={form.password}
                    onChange={setField('password')}
                    error={!!errors.password}
                    helperText={errors.password || 'Min 6 characters; share with the astrologer'}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Astrologer Profile</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Experience (years)"
                    type="number"
                    fullWidth required
                    value={form.experience}
                    onChange={setField('experience')}
                    error={!!errors.experience}
                    helperText={errors.experience}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.languages} required size="small">
                    <InputLabel>Languages</InputLabel>
                    <Select multiple value={form.languages} onChange={(e) => setForm(p => ({ ...p, languages: e.target.value }))}
                      label="Languages"
                      renderValue={(selected) => (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {selected.map(l => <Chip key={l} label={l} size="small" />)}
                        </Stack>
                      )}>
                      {languageOptions.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                    {errors.languages && <FormHelperText>{errors.languages}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nice Name"
                    fullWidth
                    value={form.niceName}
                    onChange={setField('niceName')}
                    placeholder="e.g. AstroStar, Vedic Guru"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Photos (max 10)
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {photoPreviews.map((preview, i) => (
                      <Box key={i} sx={{ position: 'relative', display: 'inline-block' }}>
                        <img src={preview} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #eee' }} />
                        <IconButton size="small" sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'white' }}
                          onClick={() => { setPhotoFiles(p => p.filter((_, j) => j !== i)); setPhotoPreviews(p => p.filter((_, j) => j !== i)); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {photoFiles.length < 10 && (
                      <Button variant="outlined" component="label" sx={{ width: 80, height: 80, borderRadius: 2, borderStyle: 'dashed', minWidth: 80 }}>
                        <input type="file" accept="image/*" hidden onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (photoFiles.length >= 10) { toast.warning('Max 10 photos'); return; }
                          setPhotoFiles(p => [...p, file]);
                          const reader = new FileReader();
                          reader.onload = (ev) => setPhotoPreviews(p => [...p, ev.target.result]);
                          reader.readAsDataURL(file);
                          e.target.value = '';
                        }} />
                        <CloudUploadIcon />
                      </Button>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={categoryData.map((c) => c.main)}
                    value={form.mainCategories}
                    onChange={(e, val) => {
                      if (val.length <= 3) {
                        setField('mainCategories')(e, val);
                        const validSubs = form.specialization.filter((s) =>
                          val.some((m) => categoryData.find((c) => c.main === m)?.subs.includes(s))
                        );
                        setForm((prev) => ({ ...prev, specialization: validSubs }));
                        if (errors.mainCategories) setErrors((prev) => ({ ...prev, mainCategories: null }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Main Category" error={!!errors.mainCategories}
                        helperText={errors.mainCategories || 'Max 3'} />
                    )}
                    renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                    ))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={categoryData.filter((c) => form.mainCategories.includes(c.main)).flatMap((c) => c.subs)}
                    value={form.specialization}
                    onChange={(e, val) => { setField('specialization')(e, val); }}
                    renderInput={(params) => (
                      <TextField {...params} label="Sub Category" error={!!errors.specialization}
                        helperText={errors.specialization} />
                    )}
                    renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                    ))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Bio"
                    fullWidth required multiline rows={3}
                    value={form.bio}
                    onChange={setField('bio')}
                    placeholder="Tell seekers about this astrologer's expertise..."
                    error={!!errors.bio}
                    helperText={errors.bio}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Pricing (₹/min)</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Chat Rate"
                    type="number"
                    fullWidth required
                    value={form.chatRate}
                    onChange={setField('chatRate')}
                    error={!!errors.chatRate}
                    helperText={errors.chatRate}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Call Rate"
                    type="number"
                    fullWidth required
                    value={form.callRate}
                    onChange={setField('callRate')}
                    error={!!errors.callRate}
                    helperText={errors.callRate}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Video Call Rate"
                    type="number"
                    fullWidth required
                    value={form.videoCallRate}
                    onChange={setField('videoCallRate')}
                    error={!!errors.videoCallRate}
                    helperText={errors.videoCallRate}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>KYC Documents</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FilePicker
                    label="Profile Image"
                    value={files.profileImage}
                    onChange={handleFileChange('profileImage')}
                    onRemove={handleFileRemove('profileImage')}
                    error={errors.profileImage}
                  />
                  {previews.profileImage && (
                    <Box mt={1}><img src={previews.profileImage} alt="preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} /></Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Initial KYC Status"
                    fullWidth
                    value={form.kycStatus}
                    onChange={setField('kycStatus')}
                    helperText="If set to Verified, the astrologer can go live immediately"
                  >
                    <MenuItem value="pending">Pending (admin will review)</MenuItem>
                    <MenuItem value="verified">Verified (auto-approve)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FilePicker
                    label="Aadhaar Card (Front)"
                    value={files.aadhaarFront}
                    onChange={handleFileChange('aadhaarFront')}
                    onRemove={handleFileRemove('aadhaarFront')}
                    error={errors.aadhaarFront}
                  />
                  {previews.aadhaarFront && (
                    <Box mt={1}><img src={previews.aadhaarFront} alt="aadhaar-front" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee' }} /></Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <FilePicker
                    label="Aadhaar Card (Back)"
                    value={files.aadhaarBack}
                    onChange={handleFileChange('aadhaarBack')}
                    onRemove={handleFileRemove('aadhaarBack')}
                    error={errors.aadhaarBack}
                  />
                  {previews.aadhaarBack && (
                    <Box mt={1}><img src={previews.aadhaarBack} alt="aadhaar-back" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee' }} /></Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FilePicker
                    label="PAN Card"
                    value={files.panCard}
                    onChange={handleFileChange('panCard')}
                    onRemove={handleFileRemove('panCard')}
                    error={errors.panCard}
                  />
                  {previews.panCard && (
                    <Box mt={1}><img src={previews.panCard} alt="pan" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee' }} /></Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" size="large" onClick={() => navigate('/astrologers')} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : null}
              sx={{ minWidth: 200 }}
            >
              {submitting ? 'Registering...' : 'Register Astrologer'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}